import { useMemo } from "react";
import {
  VoipWidget,
  type VoipWidgetAgentMe,
  type VoipWidgetHandlers,
  type VoipWidgetPresenceEntry,
} from "@pickit/voip-widget";
import { api, type AgentMe } from "./api.ts";

// WidgetHost: a thin wrapper that translates the API-driven AgentMe
// shape into the widget's expected props, and provides handlers for
// status changes + presence streaming.
//
// This is an example of how to embed @pickit/voip-widget into an
// existing site — the widget itself owns no auth or presence logic;
// the host site provides those via the `handlers` prop.

export function WidgetHost(props: { me: AgentMe; token: string; onLogout: () => void }) {
  const { me, token } = props;

  const widgetMe: VoipWidgetAgentMe = useMemo(
    () => ({
      agent: {
        id: me.agent.id,
        name: me.agent.name,
        email: me.agent.email,
        status: me.agent.status,
      },
      sip: me.sip,
      iceServers: me.iceServers,
    }),
    [me],
  );

  const handlers: VoipWidgetHandlers = useMemo(
    () => ({
      onSetStatus: async (next) => {
        await api.setMyStatus(token, next);
      },
      refreshIceServers: async () => {
        // Mint a fresh short-lived TURN credential per call. Keep the
        // STUN entries from /me (no creds, never expire) and swap in the
        // newly minted TURN entry.
        const t = await api.turnToken(token);
        const stun = me.iceServers.filter((s) => {
          const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
          return urls.every((u) => u.startsWith("stun:"));
        });
        return [...stun, { urls: t.urls, username: t.username, credential: t.credential }];
      },
      subscribePresence: (cb) => {
        // SSE stream from the control-plane; emits the full snapshot
        // on every transition. EventSource doesn't support custom
        // headers, so we use fetch + ReadableStream so we can attach
        // the bearer token.
        const ctrl = new AbortController();
        void (async () => {
          try {
            const res = await fetch("/api/agents/online/stream", {
              headers: { authorization: `Bearer ${token}` },
              signal: ctrl.signal,
            });
            if (!res.ok || !res.body) return;
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            while (true) {
              const { value, done } = await reader.read();
              if (done) break;
              buf += decoder.decode(value, { stream: true });
              let idx: number;
              while ((idx = buf.indexOf("\n\n")) !== -1) {
                const frame = buf.slice(0, idx);
                buf = buf.slice(idx + 2);
                const data = frame.split("\n").find((l) => l.startsWith("data:"));
                if (!data) continue;
                try {
                  const snap = JSON.parse(data.slice(5).trim()) as VoipWidgetPresenceEntry[];
                  cb(snap);
                } catch {
                  // ignore malformed frame
                }
              }
            }
          } catch {
            // ignore — abort or network error
          }
        })();
        return () => ctrl.abort();
      },
      // plan 57 C-2 (owner 2026-10-22) : chaque clic « Décrocher » et son issue partent au plan de contrôle (cascade du CDR +
      // journal d'audit) — best-effort, une erreur réseau ne gêne pas l'appel.
      onAnswerEvent: (ev) => {
        void api.answerEvent(token, ev as unknown as Record<string, unknown>).catch(() => undefined);
      },
      log: (level, msg, fields) => {
        // eslint-disable-next-line no-console
        console[level === "warn" ? "warn" : level === "error" ? "error" : "log"](
          `[voip] ${msg}`,
          fields ?? "",
        );
      },
      authFetch: (path, init) => {
        // Same-origin fetch with the agent's JWT attached. The widget
        // uses this to GET/POST/DELETE répondeur prompts without
        // knowing how the host site authenticates.
        const headers = new Headers(init?.headers ?? {});
        headers.set("authorization", `Bearer ${token}`);
        return fetch(path, { ...init, headers });
      },
    }),
    [token, me.iceServers],
  );

  return (
    <div className="widget-demo">
      <header className="widget-demo__header">
        <h1>Pickit<span className="accent">VoIP</span></h1>
        <div className="widget-demo__user">
          <span className="muted">{me.agent.email}</span>
          <button className="ghost" onClick={props.onLogout} type="button">Sign out →</button>
        </div>
      </header>
      <main className="widget-demo__main">
        <VoipWidget me={widgetMe} handlers={handlers} defaultDialTarget="+32" />
        <aside className="widget-demo__notes">
          <h3>This is the embeddable widget</h3>
          <p className="muted">
            Everything to the left is rendered by <code>@pickit/voip-widget</code>.
            Drop the same component into any site that's listed in the API's
            <code> CORS_ALLOW_ORIGINS</code> and it works the same.
          </p>
          <p className="muted">
            Admin features (voicemail browsing, greetings, agents, history, audit)
            live at <a href="https://admin.voip.pickitsmartly.com">admin.voip.pickitsmartly.com</a>.
          </p>
        </aside>
      </main>
    </div>
  );
}
